import {
    ConfirmModal,
    Loading,
    StyleFunction,
    useAlert,
    useAuth,
    useForm,
    useMerchantConfig,
    useModal,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    useProductFormQuery,
    useProductLazyQuery,
    useProductSaveMutation,
    useSetInventoryProductQuantityMutation,
    useIsProductPluInUseLazyQuery,
} from "graphql-sdk";
import {
    IInventoryProductInput,
    IProductInput,
    schemaInventoryProduct,
    schemaProduct,
} from "lib";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";

import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../navigation";
import { useAdminSession } from "../../../session";
import { InventoryQuantity } from "../inventory-quantity";
import { ProductChildren } from "../product-children";
import { ProductScreen } from "../product-screen";

import type { AvailableLocale } from "locales";
import { ViProduct } from "../vi/vi-product";
import { useFocusEffect } from "@react-navigation/native";
import { ProductForm } from "../form";
type ScreenProps = RootStackScreenProps<"PRODUCT_EDIT">;

export function ProductEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.product");

    const productId = route.params.id;
    const hasFocusedOnce = useRef(false);
    const lastAppliedProductId = useRef<string | null>(null);
    const [t] = useTranslation();

    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);
    const [productEdit] = useProductSaveMutation();
    const { handleMutationError } = useHandleMutationError();
    const alert = useAlert();

    const [getProduct, product] = useProductLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [checkPlu] = useIsProductPluInUseLazyQuery();
    const form = useForm<IProductInput>(schemaProduct, null);
    const {
        data: formData,
        refetch,
        loading,
    } = useProductFormQuery({
        fetchPolicy: "cache-and-network",
    });
    const inventoryForm = useForm<IInventoryProductInput>(
        schemaInventoryProduct,
        null
    );
    const [{ values }, { setDefaultValues }] = form;
    const [
        { values: inventoryValues },
        { setDefaultValues: setDefaultInventoryValues },
    ] = inventoryForm;
    const styles = useThemedStyle(styleFunc);
    const { render } = useModal();

    useEffect(() => {
        if (!productId) {
            return;
        }

        lastAppliedProductId.current = null;
    }, [productId]);

    // Inventory related props
    const [setInventoryProductQuantity, { loading: quantitySavingLoading }] =
        useSetInventoryProductQuantityMutation();

    useFocusEffect(
        useCallback(() => {
            if (!productId) {
                navigate("PRODUCTS");
                return;
            }

            if (hasFocusedOnce.current) {
                refetch();

                if (lastAppliedProductId.current !== productId) {
                    getProduct({ variables: { id: productId } });
                }
            } else {
                hasFocusedOnce.current = true;
                getProduct({ variables: { id: productId } });
            }
        }, [getProduct, navigate, productId, refetch])
    );

    useEffect(() => {
        if (values && lastAppliedProductId.current === productId) {
            return;
        }

        if (!product) {
            return;
        }

        if (product.loading) {
            return;
        }

        if (!product?.data) {
            return;
        }

        const p = product?.data.product;

        setDefaultValues({
            parentId: p.parent ? p.parent.id : null,
            groupId: p.group.id,
            externalId: p.externalId || null,
            name: p.name,
            amount: p.amount,
            minimumAmount: p.amount >= 0 ? p.minimumAmount : 0,
            costAmount: p.costAmount,
            vat: p.vat,
            ownVatId: p.ownVat?.id || null,
            onlineRequired: p.onlineRequired,
            active: p.active,
            isExternal: p.isExternal,
            ownExcludeFromDiscounts: p.ownExcludeFromDiscounts,
            barcode: p.barcode || null,
            // We always want the form to have at least one barcode field - add one if the array is empty.
            barcodes:
                p.barcodes && p.barcodes.length > 0
                    ? p.barcodes
                    : [{ barcode: "" }],
            buttonText: p.buttonText || "",
            printerId: p.printer?.id || null,
            inheritPrinter: p.inheritPrinter,
            plu: p.plu || null,
            discountTemplate: p.discountTemplate || [],
        });

        setDefaultInventoryValues({
            quantity: p.inventoryProduct?.quantity || 0,
            note: undefined,
            type: "quantity",
        });

        lastAppliedProductId.current = p.id;
    }, [
        product,
        product?.data,
        product?.loading,
        productId,
        setDefaultInventoryValues,
        setDefaultValues,
        values,
    ]);

    const edit = useCallback(async () => {
        if (!product || !product.data || !product.data.product || !values) {
            return;
        }

        if (
            product.data.product.active !== values.active &&
            values.active === false
        ) {
            const deleteResponse = await render(onClose => (
                <ConfirmModal
                    headerText={t(
                        "backoffice.products.set_inactive",
                        "Set product to inactive?"
                    )}
                    bodyText={t(
                        "backoffice.products.inactive_explain",
                        "If you choose to make this product inactive, any buttons that are added to any of your layouts, which use this product, will be removed.\nThis cannot be undone.\n\nAre you sure, you want to set this product to inactive?"
                    )}
                    onClose={onClose}
                />
            ));

            if (!deleteResponse) {
                return;
            }
        }

        if (values.plu && values.plu !== product.data.product.plu) {
            let isPluInUse;
            try {
                isPluInUse = await checkPlu({
                    variables: { plu: values.plu },
                });
            } catch (error) {
                await handleMutationError(async () => {
                    throw error;
                });
                return;
            }

            if (isPluInUse.data?.isProductPluInUse.status) {
                await alert(
                    t(
                        "backoffice.product.duplicate_plu",
                        "Duplicate PLU found"
                    ),
                    t(
                        "backoffice.product.plu_in_use",
                        "PLU {{plu}} is already in use. Please enter a different PLU or leave it blank.",
                        { plu: values.plu }
                    )
                );
                return;
            }
        }

        const saveData = {
            parentId: values.parentId,
            groupId: values.groupId,
            externalId: values.externalId,
            name: values.name,
            amount: values.amount,
            minimumAmount: values.amount >= 0 ? values.minimumAmount : 0,
            costAmount: values.costAmount,
            ownVatId: values.ownVatId || null,
            onlineRequired: values.onlineRequired,
            active: values.active,
            isExternal: values.isExternal,
            ownExcludeFromDiscounts: values.ownExcludeFromDiscounts,
            barcode: values.barcode,

            // Since there's always at least one barcode entry in the form, then we filter data before saving it.
            barcodes:
                values.barcodes
                    .filter(itm => itm.barcode.trim() !== "")
                    .map(item => ({
                        id: item.id,
                        barcode: item.barcode,
                    })) || [],
            buttonText: values.buttonText,
            printerId: values.inheritPrinter ? null : values.printerId || null,
            inheritPrinter: values.inheritPrinter,
            plu: values.plu,
            discountTemplate: values.discountTemplate
                .map(item => {
                    if (!item.name || !item.type || !item.value) {
                        // Do not save this item. It does not have the required values.
                        return;
                    }

                    return {
                        name: item.name,
                        type: item.type,
                        value: item.value,
                        color: item.color,
                    };
                })
                .filter(Boolean),
        };

        await handleMutationError(
            async () =>
                await productEdit({
                    variables: {
                        id: product.data!.product!.id,
                        product: saveData,
                    },
                }),
            t("backoffice.product.saved", "Product saved"),
            () => {
                setDefaultValues(saveData);
                navigate("PRODUCTS");
            }
        );
    }, [
        alert,
        checkPlu,
        handleMutationError,
        navigate,
        product,
        productEdit,
        render,
        setDefaultValues,
        t,
        values,
    ]);

    const handleInventoryQuantityChange = useCallback(async () => {
        if (!inventoryValues || !product.data || !product.data.product) {
            return;
        }

        const p = product.data.product;

        const invProductId = p.inventoryProduct?.id || null;

        const quantityValue = inventoryValues.quantity || 0;
        const originalValue = p.inventoryProduct?.quantity || 0;

        let quantityDelta = 0;
        if (inventoryValues.type === "quantity") {
            quantityDelta = p.inventoryProduct
                ? quantityValue - originalValue
                : quantityValue;
        } else {
            quantityDelta = quantityValue;
        }

        await handleMutationError(async () => {
            const invProduct = await setInventoryProductQuantity({
                variables: {
                    id: invProductId,
                    productId: productId,
                    quantity: quantityDelta as number,
                    note:
                        inventoryValues.note === ""
                            ? undefined
                            : inventoryValues.note,
                },
            });
            if (invProduct && invProduct.data) {
                // We have to update the local inventory product, otherwise more than 1 change to quantity (without reload) would use the the same initial quantity
                if (p.inventoryProduct) {
                    p.inventoryProduct.quantity += quantityDelta;
                } else {
                    p.inventoryProduct =
                        invProduct.data.setInventoryProductQuantity;
                }
            }
        }, t("backoffice.product.inventory_quantity_saved", "Inventory: Quantity is updated"));
    }, [
        handleMutationError,
        inventoryValues,
        product.data,
        productId,
        setInventoryProductQuantity,
        t,
    ]);

    return (
        <ProductScreen>
            <View style={styles.container}>
                <View style={styles.left}>
                    {loading ? (
                        <View style={styles.loadingContainer}>
                            <Loading />
                        </View>
                    ) : (
                        <ProductForm
                            productId={productId}
                            form={form}
                            printers={formData?.printers.data}
                            virtualPrinters={formData?.virtualPrinters.data}
                            vats={formData?.vats.data}
                            productGroups={formData?.productGroups.data}
                            products={formData?.products.data}
                            onSubmit={edit}
                            currency={currency}
                            submitButton={["common.save", "Save"]}
                        />
                    )}
                </View>
                <View style={styles.right}>
                    {product?.data?.product?.children?.data &&
                    product?.data?.product?.children?.data.length > 0 ? (
                        <ProductChildren
                            childProducts={product.data.product.children.data}
                            currency={currency}
                            locale={locale as AvailableLocale}
                        />
                    ) : null}
                    <InventoryQuantity
                        form={inventoryForm}
                        onSubmit={handleInventoryQuantityChange}
                        unit={t(
                            "backoffice.product.inventory_unit_pcs",
                            "Pcs."
                        )}
                        loading={quantitySavingLoading}
                    />
                    {auth.may("merchant.vi") && (
                        <ViProduct
                            style={styles.viProduct}
                            productId={productId}
                        />
                    )}
                </View>
            </View>
        </ProductScreen>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    loadingContainer: {
        width: theme.dimensions.maxFormWidth,
    },
    left: {
        marginBottom: theme.spacingScale * 2,
        marginRight: theme.spacingScale * 3,
    },
    right: {
        minWidth: 250,
    },
    viProduct: {
        marginVertical: theme.spacingScale,
    },
});
